import React, { useState, useEffect } from "react";

const DCFCalculator = () => {
  const [year1CashFlow, setYear1CashFlow] = useState(100000);
  const [year2CashFlow, setYear2CashFlow] = useState(110000);
  const [year3CashFlow, setYear3CashFlow] = useState(121000);
  const [year4CashFlow, setYear4CashFlow] = useState(133000);
  const [year5CashFlow, setYear5CashFlow] = useState(146000);
  const [terminalGrowthRate, setTerminalGrowthRate] = useState(3);
  const [discountRate, setDiscountRate] = useState(10);
  const [periodType, setPeriodType] = useState("annual");
  const [result, setResult] = useState({
    terminalValue: "0.00",
    pvTerminalValue: "0.00",
    pvForecastPeriod: "0.00",
    enterpriseValue: "0.00",
    totalCashFlow: "0.00",
    avgGrowthRate: "0.00",
    valuationCategory: "",
    investmentInsight: "",
  });

  // DCF Calculation Function
  const calculateDCF = () => {
    const cf1 = parseFloat(year1CashFlow);
    const cf2 = parseFloat(year2CashFlow);
    const cf3 = parseFloat(year3CashFlow);
    const cf4 = parseFloat(year4CashFlow);
    const cf5 = parseFloat(year5CashFlow);
    const terminalGrowth = parseFloat(terminalGrowthRate) / 100;
    const discount = parseFloat(discountRate) / 100;

    if (isNaN(discount) || discount <= 0 || discount >= 1) return;

    const cashFlows = [cf1, cf2, cf3, cf4, cf5];
    const totalCashFlow = cashFlows.reduce((sum, cf) => sum + cf, 0);

    // Calculate Present Value of forecast period cash flows
    let pvForecastPeriod = 0;
    for (let i = 0; i < cashFlows.length; i++) {
      pvForecastPeriod += cashFlows[i] / Math.pow(1 + discount, i + 1);
    }

    // Calculate Terminal Value using Gordon Growth Model
    // Terminal Value = CF(Year 5) × (1 + g) / (r - g)
    const terminalValue = (cf5 * (1 + terminalGrowth)) / (discount - terminalGrowth);
    
    // Discount Terminal Value to present
    const pvTerminalValue = terminalValue / Math.pow(1 + discount, 5);

    // Enterprise Value = PV of Forecast Period + PV of Terminal Value
    const enterpriseValue = pvForecastPeriod + pvTerminalValue;

    // Calculate average growth rate
    let avgGrowthRate = 0;
    if (cf1 > 0) {
      avgGrowthRate = (Math.pow(cf5 / cf1, 1/4) - 1) * 100;
    }

    let valuationCategory = "";
    let investmentInsight = "";

    // Terminal value as percentage of enterprise value
    const terminalPercentage = (pvTerminalValue / enterpriseValue) * 100;

    if (terminalPercentage > 80) {
      valuationCategory = "Terminal Heavy";
      investmentInsight = "Value heavily dependent on terminal assumptions";
    } else if (terminalPercentage > 60) {
      valuationCategory = "Balanced Growth";
      investmentInsight = "Reasonable mix of near-term and long-term value";
    } else {
      valuationCategory = "Near-term Focus";
      investmentInsight = "Value driven by forecast period cash flows";
    }

    setResult({
      terminalValue: terminalValue.toFixed(2),
      pvTerminalValue: pvTerminalValue.toFixed(2),
      pvForecastPeriod: pvForecastPeriod.toFixed(2),
      enterpriseValue: enterpriseValue.toFixed(2),
      totalCashFlow: totalCashFlow.toFixed(2),
      avgGrowthRate: avgGrowthRate.toFixed(2),
      valuationCategory,
      investmentInsight,
    });
  };

  useEffect(() => {
    calculateDCF();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
          </div>
          <h1>DCF Calculator (Discounted Cash Flow)</h1>
          <p>
            Calculate enterprise value by discounting future cash flows to present value
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Free Cash Flow Projections
              </h2>

              {/* Year 1 Cash Flow */}
              <div className="input-group">
                <label>Year 1 Free Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year1CashFlow}
                    onChange={(e) => setYear1CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[100000, 500000, 1000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setYear1CashFlow(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Year 2 Cash Flow */}
              <div className="input-group">
                <label>Year 2 Free Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year2CashFlow}
                    onChange={(e) => setYear2CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="110000"
                  />
                </div>
              </div>

              {/* Year 3 Cash Flow */}
              <div className="input-group">
                <label>Year 3 Free Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year3CashFlow}
                    onChange={(e) => setYear3CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="121000"
                  />
                </div>
              </div>

              {/* Year 4 Cash Flow */}
              <div className="input-group">
                <label>Year 4 Free Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year4CashFlow}
                    onChange={(e) => setYear4CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="133000"
                  />
                </div>
              </div>

              {/* Year 5 Cash Flow */}
              <div className="input-group">
                <label>Year 5 Free Cash Flow</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={year5CashFlow}
                    onChange={(e) => setYear5CashFlow(e.target.value)}
                    className="calc-input"
                    placeholder="146000"
                  />
                </div>
              </div>

              {/* Terminal Growth Rate */}
              <div className="input-group">
                <label>Terminal Growth Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={terminalGrowthRate}
                    onChange={(e) => setTerminalGrowthRate(e.target.value)}
                    className="calc-input"
                    placeholder="3"
                    step="0.5"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[2, 3, 4, 5].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setTerminalGrowthRate(rate)}
                      className="quick-select-btn success"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Discount Rate (WACC) */}
              <div className="input-group">
                <label>Discount Rate (WACC)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={discountRate}
                    onChange={(e) => setDiscountRate(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                    step="0.5"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 15].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setDiscountRate(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Period Type */}
              <div className="input-group">
                <label>Cash Flow Period</label>
                <div className="quick-select-group">
                  {[
                    { value: "annual", label: "Annual" },
                    { value: "quarterly", label: "Quarterly" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPeriodType(type.value)}
                      className={`quick-select-btn ${periodType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateDCF} className="calculate-btn">
                Calculate Enterprise Value
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Valuation Analysis
              </h2>

              <div className="result-card">
                {/* Enterprise Value - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Enterprise Value</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.enterpriseValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.valuationCategory}
                  </div>
                </div>

                {/* PV Forecast Period */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">PV of Forecast Period</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.pvForecastPeriod).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Years 1-5 discounted value
                  </div>
                </div>

                {/* PV Terminal Value */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">PV of Terminal Value</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                        <polyline points="17 6 23 6 23 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.pvTerminalValue).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Beyond year 5 value
                  </div>
                </div>

                {/* Average Growth Rate */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Avg Growth Rate</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.avgGrowthRate}%
                  </div>
                  <div className="result-subtext">
                    Forecast period CAGR
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Value Composition</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Forecast Period Value</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.pvForecastPeriod) / parseFloat(result.enterpriseValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${(parseFloat(result.pvForecastPeriod) / parseFloat(result.enterpriseValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Terminal Value</span>
                      <span className="breakdown-percentage">
                        {((parseFloat(result.pvTerminalValue) / parseFloat(result.enterpriseValue)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${(parseFloat(result.pvTerminalValue) / parseFloat(result.enterpriseValue)) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Terminal Value (Undiscounted)</span>
                      <span className="breakdown-percentage">
                        ₹{Number(result.terminalValue).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill purple"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Insight Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #3b82f6'}}>
                  <h3 className="breakdown-title" style={{color: '#1e40af', marginBottom: '0.5rem'}}>Valuation Insight</h3>
                  <p style={{fontSize: '0.875rem', color: '#1e3a8a', lineHeight: '1.5', margin: 0}}>
                    {result.investmentInsight}. Terminal value represents {((parseFloat(result.pvTerminalValue) / parseFloat(result.enterpriseValue)) * 100).toFixed(1)}% of enterprise value. 
                    Forecast period cash flows grow at {result.avgGrowthRate}% CAGR, while terminal growth is set at {terminalGrowthRate}%.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Discounted Cash Flow (DCF)</h3>
              <p>
                Discounted Cash Flow (DCF) is a valuation method that calculates the present value of expected future 
                cash flows to determine a company's or investment's intrinsic value. It's based on the principle that 
                money today is worth more than the same amount in the future due to earning potential. DCF involves 
                two main components: forecast period (typically 5-10 years of projected free cash flows) and terminal 
                value (representing value beyond the forecast period). Free cash flows are discounted back to present 
                value using WACC (Weighted Average Cost of Capital) as the discount rate. Terminal value is calculated 
                using the Gordon Growth Model, assuming perpetual growth at a sustainable rate (typically GDP growth 
                rate or inflation). DCF is widely used in equity research, M&A, private equity, and investment banking 
                for company valuation. It's considered more rigorous than simple multiple-based valuations as it accounts 
                for time value of money and company-specific projections rather than market comparables.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>DCF Formulas:</h4>
            <div className="formula-display">
              Enterprise Value = Σ [FCFt ÷ (1 + WACC)^t] + [Terminal Value ÷ (1 + WACC)^n]
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">FCF</div>
                <div className="formula-param-value">Free Cash Flow (operating cash flow - capex)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">WACC</div>
                <div className="formula-param-value">Weighted Average Cost of Capital (discount rate)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Terminal Value</div>
                <div className="formula-param-value">FCF(last year) × (1 + g) ÷ (WACC - g)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Equity Value</div>
                <div className="formula-param-value">Enterprise Value - Net Debt + Cash</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is Free Cash Flow (FCF) in DCF analysis?</h5>
              <p>
                Free Cash Flow is the cash a company generates after accounting for operating expenses and capital expenditures. It represents actual cash available to investors and is calculated as Operating Cash Flow minus Capital Expenditures (CapEx).
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I determine the appropriate discount rate (WACC)?</h5>
              <p>
                WACC is the weighted average of cost of equity and cost of debt, reflecting the company's capital structure. For established companies, use 8-12%; for startups or high-risk ventures, use 15-25%. It should reflect the risk profile of the business.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is Terminal Value and why is it important?</h5>
              <p>
                Terminal Value represents the present value of all future cash flows beyond the forecast period, assuming perpetual growth. It typically accounts for 60-80% of total enterprise value, making terminal growth rate assumptions critical to valuation accuracy.
              </p>
            </div>

            <div className="faq-item">
              <h5>What terminal growth rate should I use?</h5>
              <p>
                Terminal growth rate should be conservative, typically 2-4%, aligned with long-term GDP growth or inflation. Using rates higher than GDP growth implies the company will eventually exceed the entire economy, which is unrealistic.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I convert Enterprise Value to Equity Value?</h5>
              <p>
                Equity Value = Enterprise Value - Net Debt + Cash. Enterprise value represents total business value, while equity value is what belongs to shareholders. Subtract debt (obligation to creditors) and add cash (already owned by company).
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the limitations of DCF valuation?</h5>
              <p>
                DCF is highly sensitive to assumptions (growth rates, WACC, terminal value). Small changes can dramatically alter valuation. It's also difficult to forecast cash flows accurately for 5+ years, especially for early-stage or cyclical businesses with unpredictable futures.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DCFCalculator;